import { TransitionGroup } from "react-transition-group";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemProps,
  ListItemText,
  ListProps,
  listItemButtonClasses,
  styled,
  svgIconClasses,
  typographyClasses,
} from "@mui/material";

import { DateTime } from "./primitives/DateTime";
import { Patient } from "../models/patient";
import { PatientData } from "../models/patient-data";
import { useAllPatientData } from "../queries/patients-query";
import { useAppContext } from "../context/AppContext";
import { format } from "date-fns";
import { PatientExportsMenu } from "./patient-exports";

const StyledList = styled(List)<{ component?: React.ElementType }>(
  ({ theme }) => ({
    maxHeight: 200,
    overflow: "auto",
    padding: theme.spacing(1, 2),
    scrollbarWidth: "thin",
    [`& .${listItemButtonClasses.root}:hover`]: {
      background: "transparent",
      [`& .${typographyClasses.root}`]: {
        color: theme.palette.primary.main,
      },
      [`& .${svgIconClasses.root}`]: {
        color: theme.palette.primary.main,
      },
    },
  }),
);

const PatientExport: React.FC<
  ListItemProps & {
    id: string;
    patient: Patient;
    exportDate: PatientData["exportDate"];
    isSelected?: boolean;
    onButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
  }
> = ({ id, patient, exportDate, isSelected, onButtonClick, ...rootProps }) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (isSelected)
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
  }, [isSelected]);

  return (
    <ListItem
      disableGutters
      disablePadding
      divider
      {...rootProps}
      ref={ref}
      secondaryAction={
        <PatientExportsMenu
          patient={patient}
          id={id}
          date={format(exportDate, "yyyy-MM-dd HH:mm")}
        />
      }
    >
      <ListItemButton onClick={onButtonClick}>
        <ListItemIcon sx={({ spacing }) => ({ minWidth: spacing(4) })}>
          {isSelected ? (
            <RadioButtonCheckedIcon
              fontSize="inherit"
              sx={{ color: "primary.main" }}
            />
          ) : (
            <RadioButtonUncheckedIcon fontSize="inherit" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={<DateTime timestamp={exportDate.getTime()} />}
          primaryTypographyProps={{
            color: isSelected ? "primary" : "inherit",
            component: "div",
            style: { fontVariantNumeric: "tabular-nums" },
            variant: "body2",
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

export const PatientExports: React.FC<ListProps & { patient: Patient }> = ({
  patient,
}) => {
  const { t } = useTranslation();
  const { dataConfig, setDataConfig } = useAppContext();
  const { data: exports } = useAllPatientData(patient?.id);

  const onImportClick = useCallback(
    (exportId: PatientData["exportId"]) =>
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setDataConfig((s) => ({ ...s, exportId }));
      },
    [setDataConfig],
  );

  const onAttemptNewImport = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setDataConfig((s) => ({ ...s, exportId: null }));
    },
    [setDataConfig],
  );

  const exportsElements = useMemo(() => {
    if (!exports || exports.length === 0) return;
    return (
      <TransitionGroup>
        {exports
          ?.sort(
            (a: PatientData, b: PatientData) =>
              b.exportDate.getTime() - a.exportDate.getTime(),
          )
          .map((patientExport: PatientData) => {
            const isSelected = patientExport.exportId === dataConfig.exportId;
            return (
              <Collapse key={patientExport.exportId}>
                <PatientExport
                  id={patientExport.exportId}
                  patient={patient}
                  exportDate={patientExport.exportDate}
                  isSelected={isSelected}
                  onButtonClick={onImportClick(patientExport.exportId)}
                />
              </Collapse>
            );
          })}
      </TransitionGroup>
    );
  }, [dataConfig.exportId, patient, exports, onImportClick]);

  if (!exports) return;

  return (
    <StyledList dense>
      {exportsElements}
      <ListItem disablePadding disableGutters>
        <ListItemButton
          disabled={exports.length < 1}
          onClick={onAttemptNewImport}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("patient.import-cta")}
            primaryTypographyProps={{ variant: "body2" }}
          />
        </ListItemButton>
      </ListItem>
    </StyledList>
  );
};
